import { AsyncSelect } from '@tempo-io/sui-select';
import { toast } from '@tempo-io/sui-sonner';
import debounce from 'lodash.debounce';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSObjectWithLabel } from 'react-select';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import { ItemsApi } from '@/lib/api/common/items';
import type { ItemFiltersType } from '@/lib/api/types/itemFilters';
import type { ItemType, ItemTypeLevel } from '@/lib/api/types/itemType';
import { useItemTypeLevelsQuery } from '@/lib/queries/common/useItemTypeLevelsQuery';
import { useItemTypesQuery } from '@/lib/queries/common/useItemTypesQuery';

export type ParentItemOption = { id: number; name: string; itemTypeId: number };

interface ParentItemAutocompleteProps {
  value: ParentItemOption | null;
  onChange: (item: ParentItemOption | null) => void;
  validParentLevel: number;
  loopId?: number;
  excludedItemIds?: number[];
  disabled?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}

const SEARCH_RESULTS_LIMIT = 10;
const DEBOUNCE_DELAY_MS = 300;
const MENU_PORTAL_Z_INDEX = 3800;
const MENU_PORTAL_STYLES = {
  menuPortal: (base: CSSObjectWithLabel) => ({
    ...base,
    zIndex: MENU_PORTAL_Z_INDEX,
    pointerEvents: 'auto' as const,
  }),
};

function getValidParentItemTypeIds(
  itemTypes: ItemType[],
  itemTypeLevels: ItemTypeLevel[],
  validParentLevel: number,
): number[] {
  return itemTypes
    .filter((type) => {
      const level = itemTypeLevels.find((l) => l.id === type.itemLevelId);
      return level?.level === validParentLevel;
    })
    .map((type) => type.id);
}

function buildParentItemFilters(
  inputValue: string,
  validParentItemTypeIds: number[],
  loopId?: number,
): ItemFiltersType {
  const filters: ItemFiltersType = {};

  if (inputValue.trim()) {
    filters.name = { value: inputValue.trim(), operator: 'startsWithIgnoreCase' };
  }

  if (validParentItemTypeIds.length > 0) {
    filters.itemTypeId = { value: validParentItemTypeIds, operator: 'in' };
  }

  if (loopId !== undefined) {
    filters.loopId = { value: [loopId], operator: 'in' };
  }

  return filters;
}

export const ParentItemAutocomplete = ({
  value,
  onChange,
  validParentLevel,
  loopId,
  excludedItemIds = [],
  disabled,
  onMenuOpen,
  onMenuClose,
}: ParentItemAutocompleteProps) => {
  const { t } = useTranslation();
  const { data: itemTypes = [] } = useItemTypesQuery();
  const { data: itemTypeLevels = [] } = useItemTypeLevelsQuery();

  const validParentItemTypeIds = useMemo(
    () => getValidParentItemTypeIds(itemTypes, itemTypeLevels, validParentLevel),
    [itemTypes, itemTypeLevels, validParentLevel],
  );
  const cacheKey = useMemo(
    () => `${[...validParentItemTypeIds].sort((a, b) => a - b).join('-')}:${loopId ?? ''}`,
    [validParentItemTypeIds, loopId],
  );
  const excludedItemIdSet = useMemo(() => new Set(excludedItemIds), [excludedItemIds]);

  const fetchItems = useMemo(
    () =>
      debounce((inputValue: string, callback: (options: ParentItemOption[]) => void) => {
        const fetchAsync = async () => {
          if (validParentItemTypeIds.length === 0) {
            callback([]);
            return;
          }

          try {
            const filters = buildParentItemFilters(inputValue, validParentItemTypeIds, loopId);
            const response = await ItemsApi.getItems({
              filter: filters,
              order: 'name',
              limit: SEARCH_RESULTS_LIMIT,
              hierarchical: false,
            });
            callback(
              response.data
                .filter((item) => !excludedItemIdSet.has(item.id))
                .map((item) => ({
                  id: item.id,
                  name: item.name,
                  itemTypeId: item.itemTypeId,
                })),
            );
          } catch (_error) {
            toast.error('Failed to load parent items');
            callback([]);
          }
        };
        fetchAsync();
      }, DEBOUNCE_DELAY_MS),
    [excludedItemIdSet, validParentItemTypeIds, loopId],
  );

  return (
    <AsyncSelect<ParentItemOption>
      key={cacheKey}
      height={40}
      cacheOptions
      defaultOptions={!disabled && validParentItemTypeIds.length > 0}
      loadOptions={fetchItems}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      // @ts-expect-error sui-select omits `styles` from its public type
      styles={MENU_PORTAL_STYLES}
      value={value}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id.toString()}
      formatOptionLabel={(option) => {
        const itemType = itemTypes.find((t) => t.id === option.itemTypeId);
        return (
          <div className="flex items-center gap-2">
            {itemType && <ItemTypeIcon itemType={itemType} className="h-4 w-4" />}
            <span>{option.name}</span>
          </div>
        );
      }}
      onChange={onChange}
      isClearable={false}
      placeholder={t(($) => $.createItem.fields.parent.placeholder)}
      isDisabled={disabled}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      noOptionsMessage={() => t(($) => $.createItem.fields.parent.emptyMessage)}
    />
  );
};
